import { useLocation, useNavigate } from 'react-router-dom';
import {
    Home,
    Camera,
    Briefcase,
    Users,
    Smartphone,
    Mail,
    Tag,
    Building2,
    ToggleLeft,
    KeyRound,
    FileText,
    Megaphone,
    Settings,
    Activity,
    UserCircle,
    Monitor,
    PanelLeftClose,
    PanelLeftOpen,
} from 'lucide-react';

import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarSeparator,
    useSidebar,
} from '@/src/shadComponents/ui/sidebar';
import FeatureFlag from '@/src/Features/components/FeatureFlag';
import { FeatureFlags } from '@/src/Features/lib/constants';
import logo from '../../smile_icon.png';
import ThemePicker from './ThemePicker';

export default function AppSidebar() {
    const navigate = useNavigate();
    const location = useLocation();
    const { toggleSidebar, open } = useSidebar();

    const isActive = (path: string) => {
        if (path === '/home') return location.pathname === '/' || location.pathname === '/home';
        return location.pathname.startsWith(path);
    };

    return (
        <Sidebar collapsible="icon">
            <SidebarHeader>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton
                            onClick={() => navigate('/home')}
                            tooltip="Home"
                        >
                            <div
                                className="size-4 shrink-0 bg-sidebar-foreground"
                                style={{
                                    maskImage: `url(${logo})`,
                                    maskSize: 'contain',
                                    maskRepeat: 'no-repeat',
                                    maskPosition: 'center',
                                    WebkitMaskImage: `url(${logo})`,
                                    WebkitMaskSize: 'contain',
                                    WebkitMaskRepeat: 'no-repeat',
                                    WebkitMaskPosition: 'center',
                                }}
                                role="img"
                                aria-label="Photostat"
                            />
                            <span className="font-bold text-base tracking-tight">Photostat</span>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>

            <SidebarContent>
                {/* Main Navigation */}
                <SidebarGroup>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    isActive={isActive('/home')}
                                    onClick={() => navigate('/home')}
                                    tooltip="Dashboard"
                                >
                                    <Home />
                                    <span>Dashboard</span>
                                </SidebarMenuButton>
                            </SidebarMenuItem>

                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    isActive={isActive('/command-center')}
                                    onClick={() => navigate('/command-center')}
                                    tooltip="Command Center"
                                >
                                    <Monitor />
                                    <span>Command Center</span>
                                </SidebarMenuButton>
                            </SidebarMenuItem>

                            <FeatureFlag flag={FeatureFlags.JOBS_VIEWER_V1}>
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        isActive={isActive('/jobs')}
                                        onClick={() => navigate('/jobs')}
                                        tooltip="Jobs"
                                    >
                                        <Briefcase />
                                        <span>Jobs</span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </FeatureFlag>

                            <FeatureFlag flag={FeatureFlags.ACTIVATIONS_VIEWER_V1}>
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        isActive={isActive('/activations')}
                                        onClick={() => navigate('/activations')}
                                        tooltip="Activations"
                                    >
                                        <Camera />
                                        <span>Activations</span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </FeatureFlag>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>

                <SidebarSeparator />

                {/* Settings Section */}
                <SidebarGroup>
                    <SidebarGroupLabel>Settings</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <FeatureFlag flag={FeatureFlags.ACCOUNT_ADMIN_V1}>
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        isActive={isActive('/accounts')}
                                        onClick={() => navigate('/accounts')}
                                        tooltip="Accounts"
                                    >
                                        <Users />
                                        <span>Accounts</span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </FeatureFlag>

                            <FeatureFlag flag={FeatureFlags.DEVICE_VIEWER_V1}>
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        isActive={isActive('/device')}
                                        onClick={() => navigate('/device-management')}
                                        tooltip="Devices"
                                    >
                                        <Smartphone />
                                        <span>Devices</span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </FeatureFlag>

                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    isActive={isActive('/templates')}
                                    onClick={() => navigate('/templates')}
                                    tooltip="Email Templates"
                                >
                                    <Mail />
                                    <span>Email Templates</span>
                                </SidebarMenuButton>
                            </SidebarMenuItem>

                            <FeatureFlag flag={FeatureFlags.WHITELABEL_SETTINGS_V1}>
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        isActive={isActive('/org/whitelabel')}
                                        onClick={() => navigate('/org/whitelabel-settings')}
                                        tooltip="Whitelabel"
                                    >
                                        <Tag />
                                        <span>Whitelabel</span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </FeatureFlag>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>

                {/* Admin Section */}
                <FeatureFlag flag={FeatureFlags.GLOBAL_ADMIN_V1}>
                    <>
                    <SidebarSeparator />
                    <SidebarGroup>
                        <SidebarGroupLabel>Admin</SidebarGroupLabel>
                        <SidebarGroupContent>
                            <SidebarMenu>
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        isActive={isActive('/admin/orgs')}
                                        onClick={() => navigate('/admin/orgs')}
                                        tooltip="Organizations"
                                    >
                                        <Building2 />
                                        <span>Organizations</span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        isActive={isActive('/admin/features')}
                                        onClick={() => navigate('/admin/features')}
                                        tooltip="Features"
                                    >
                                        <ToggleLeft />
                                        <span>Features</span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        isActive={isActive('/admin/licenses')}
                                        onClick={() => navigate('/admin/licenses')}
                                        tooltip="Licenses"
                                    >
                                        <KeyRound />
                                        <span>Licenses</span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        isActive={isActive('/admin/templates')}
                                        onClick={() => navigate('/admin/templates')}
                                        tooltip="Templates"
                                    >
                                        <FileText />
                                        <span>Templates</span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        isActive={isActive('/admin/announcements')}
                                        onClick={() => navigate('/admin/announcements')}
                                        tooltip="Announcements"
                                    >
                                        <Megaphone />
                                        <span>Announcements</span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        isActive={isActive('/admin/global-settings')}
                                        onClick={() => navigate('/admin/global-settings')}
                                        tooltip="Global Settings"
                                    >
                                        <Settings />
                                        <span>Global Settings</span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        isActive={isActive('/admin/system-health')}
                                        onClick={() => navigate('/admin/system-health')}
                                        tooltip="System Health"
                                    >
                                        <Activity />
                                        <span>System Health</span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                <FeatureFlag flag={FeatureFlags.GALLERY_USER_MANAGER_V1}>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton
                                            isActive={isActive('/admin/gallery-users')}
                                            onClick={() => navigate('/admin/gallery-users')}
                                            tooltip="Gallery Users"
                                        >
                                            <UserCircle />
                                            <span>Gallery Users</span>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                </FeatureFlag>
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                    </>
                </FeatureFlag>
            </SidebarContent>

            <SidebarFooter>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <ThemePicker />
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                        <SidebarMenuButton
                            onClick={toggleSidebar}
                            tooltip={open ? 'Collapse' : 'Expand'}
                        >
                            {open ? <PanelLeftClose /> : <PanelLeftOpen />}
                            <span>Collapse</span>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarFooter>
        </Sidebar>
    );
}
